import { useId, forwardRef } from 'react';
import { SingleValue } from 'react-select';
import styles from './InputDateSelect.module.css';
import { useDateSelector } from './hooks/UseDateSelector';
import { InputDateSelectElement } from './input-date-select-element';
import { FormErrorMessage } from '../form-error-message';
import { clsx } from 'clsx';
import { FORMAT_KEY, getSelectOrder } from './utils/FormatRetrievers';
import { InputDateSelectProps } from './types/InputDateSelectTypes';
import { INPUT_DATE_SELECT_FORMAT, InputSelectOption } from './types/InputDateSelectTransformerTypes';

export const InputDateSelect = forwardRef<HTMLDivElement, InputDateSelectProps>(
	(
		{
			label,
			errorMessage,
			id,
			minDate,
			maxDate,
			locale,
			value,
			onBlur,
			onChange,
			ariaLabel,
			placeholders,
			className,
			disabled = false,
			format = INPUT_DATE_SELECT_FORMAT.ISO_STRING,
			name
		},
		ref
	) => {
		const generateIdentifier = useId();
		const identifier = id ?? generateIdentifier;
		const {
			daysOptions,
			monthOptions,
			yearOptions,
			selectedDay,
			selectedMonth,
			selectedYear,
			handleDayChange,
			handleMonthChange,
			handleYearChange
		} = useDateSelector({ minDate, maxDate, placeholders, value, onChange, format });

		const errorMessageIdentifier = errorMessage ? identifier + '-error' : undefined;

		return (
			<div className={className}>
				<div
					className={clsx(styles.container, disabled && styles.containerDisabled, {
						[styles.errorBorder]: Boolean(errorMessage)
					})}
					ref={ref}
					onBlur={(evt) => {
						if (!evt.currentTarget.contains(evt.relatedTarget)) {
							onBlur?.(evt);
						}
					}}
				>
					<label htmlFor={identifier + '-input'} className={styles.selectorsLabel}>
						{label}
					</label>
					<div id={identifier + '-input'} className={styles.selectors}>
						{getSelectOrder({ locale, format }).map((key) => {
							if (key === FORMAT_KEY.DAY) {
								return (
									<InputDateSelectElement
										key={key}
										name={`${name}-day`}
										options={daysOptions}
										onChange={(selectedOption: SingleValue<InputSelectOption>) => {
											handleDayChange(selectedOption);
										}}
										value={selectedDay}
										maxLength={2}
										disabled={disabled}
										ariaLabel={ariaLabel?.day}
										aria-errormessage={errorMessageIdentifier}
										aria-invalid={Boolean(errorMessage)}
									/>
								);
							}
							if (key === FORMAT_KEY.MONTH) {
								return (
									<InputDateSelectElement
										key={key}
										name={`${name}-month`}
										options={monthOptions}
										onChange={(selectedOption: SingleValue<InputSelectOption>) => {
											handleMonthChange(selectedOption);
										}}
										value={selectedMonth}
										maxLength={2}
										disabled={disabled}
										ariaLabel={ariaLabel?.month}
										aria-errormessage={errorMessageIdentifier}
										aria-invalid={Boolean(errorMessage)}
									/>
								);
							}

							return (
								<InputDateSelectElement
									key={key}
									name={`${name}-year`}
									options={yearOptions}
									onChange={(selectedOption: SingleValue<InputSelectOption>) => {
										handleYearChange(selectedOption);
									}}
									value={selectedYear}
									maxLength={4}
									disabled={disabled}
									ariaLabel={ariaLabel?.year}
									aria-errormessage={errorMessageIdentifier}
									aria-invalid={Boolean(errorMessage)}
								/>
							);
						})}
					</div>
				</div>
				{errorMessage && <FormErrorMessage id={errorMessageIdentifier}>{errorMessage}</FormErrorMessage>}
			</div>
		);
	}
);

InputDateSelect.displayName = 'InputDateSelect';
